import { useCallback, useRef } from 'react';
import { FilterOptionsState } from '@material-ui/lab';

import environment from '../environments';
import { Task } from '../secure-portal/tasks/Task.interface';
import { WorkflowEntity } from '../secure-portal/workflow/Workflow.interface';
import { ApiParams } from './api/api';
import { FetchApiCallback } from './api/Api.hooks';

export const ENTITY_KEY_REGEXP: RegExp = /^[\w\d]+-[\d]+$/i;

export type QMSEntity = WorkflowEntity | keyof typeof WorkflowEntity | 'User';

export function getEntityPrefix(entity: QMSEntity): string {
  switch (entity) {
    case 'User':
      return 'USER';
    case WorkflowEntity.Capa:
      return 'CAPA';
    case WorkflowEntity.Risk:
      return 'RISK';
    case WorkflowEntity.Training:
      return 'TRAINING';
    case WorkflowEntity.Supplier:
      return 'SUPPLIER';
    case WorkflowEntity.Scar:
      return 'SCAR';
    case WorkflowEntity.Spor:
      return 'SPOR';
    // product
    case WorkflowEntity.Product:
      return 'PRODUCT';
    case WorkflowEntity.UserNeed:
      return environment.productConfig?.userNeed?.idPrefix || 'UN';
    case WorkflowEntity.DesignInput:
      return environment.productConfig?.designInput?.idPrefix || 'IN';
    case WorkflowEntity.DesignOutput:
      return environment.productConfig?.designOutput?.idPrefix || 'OUT';
    case WorkflowEntity.DesignVerification:
      return environment.productConfig?.verification?.idPrefix || 'VER';
    case WorkflowEntity.DesignValidation:
      return environment.productConfig?.validation?.idPrefix || 'VAL';
    case WorkflowEntity.DesignSpec:
      return environment.productConfig?.designSpec?.idPrefix || 'DS';
    // hazard
    case WorkflowEntity.Hazard:
      return 'HAZARD';
    case WorkflowEntity.Cause:
      return 'CAUSE';
    case WorkflowEntity.Control:
      return 'CONTROL';
    case WorkflowEntity.Action:
      return 'ACTION';
    case WorkflowEntity.ChangeOrder:
      return 'CO';
    default:
      return '';
  }
}

export function transformId(prefix: string, search: string): string | number {
  const key: string = search.trim();
  // prefix should exist
  // key should start with the {prefix}-
  // key should match entity regexp
  if (
    !prefix ||
    !key.toLowerCase().startsWith(prefix.toLowerCase().concat('-')) ||
    !ENTITY_KEY_REGEXP.test(key)
  ) {
    return search;
  }
  const [, entityId = ''] = key.split('-');
  const id: number = parseInt(entityId);
  // should not have leading zeros
  return isFinite(id) && id.toString() === entityId ? id : search;
}

export function useTransformId(
  entity: QMSEntity
): (search: string) => string | number {
  return useCallback(
    (search: string) => transformId(getEntityPrefix(entity), search),
    [entity]
  );
}

export interface IdSearchableFetchOptions {
  entity: QMSEntity;
  // transform `from` property to ID and set to `to` property
  from?: keyof ApiParams;
  to?: keyof ApiParams;
  // delete `from` property from params if `to` property is set
  removeFrom?: boolean;
}

export function transformParams(
  params: ApiParams | undefined,
  options: IdSearchableFetchOptions
): ApiParams | undefined {
  const {
    entity,
    from = 'search',
    to = 'searchId',
    removeFrom = true
  } = options;
  if (typeof params?.[from] === 'string') {
    const prefix: string = getEntityPrefix(entity);
    const id: string | number = transformId(prefix, params[from]);
    if (typeof id === 'number') {
      // create copy when updating params
      params = { ...params };
      params[to] = id;
      removeFrom && delete params[from];
    }
  }
  return params;
}

// transform 'search' property to ID as 'searchId'
export function useIdSearchableFetch(
  callback: FetchApiCallback,
  entity: QMSEntity | IdSearchableFetchOptions | IdSearchableFetchOptions[]
): FetchApiCallback {
  const entityRef = useRef<typeof entity>(entity);
  entityRef.current = entity;

  const fetch: FetchApiCallback = useCallback(
    (params?: ApiParams) => {
      const { current: entity } = entityRef;
      const allOptions: IdSearchableFetchOptions[] = Array.isArray(entity)
        ? entity
        : typeof entity === 'object'
        ? [entity]
        : [{ entity }];
      for (const options of allOptions) {
        params = transformParams(params, options);
      }
      callback(params);
    },
    [callback]
  );

  return fetch;
}

// use for Autocomplete.filterOptions
export function useIdSearchableFilterOptions<
  Entity extends { id?: number; task?: Task; [key: string]: any }
>(entity: QMSEntity, propToMatch: keyof Entity) {
  return useCallback(
    (options: Entity[], { inputValue }: FilterOptionsState<Entity>) => {
      const id: string | number = transformId(
        getEntityPrefix(entity),
        inputValue
      );
      return options.filter((option: Entity) => {
        if (typeof id === 'number') {
          return id === option.id;
        }
        const input: string = inputValue.toLowerCase();
        return (
          option.task?.key?.toLowerCase().includes(input) ||
          option[propToMatch]?.toLowerCase().includes(input)
        );
      });
    },
    [entity, propToMatch]
  );
}
